import { useEffect } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useAppDispatch, useAppSelector } from '@/store';
import type { RootState } from '@/store';
import { fetchCiternes } from '@/store/thunks/citerneThunks';
import { fetchTiers } from '@/store/thunks/tierThunks';
import { createVente, updateVente } from '@/store/thunks/movementThunks';
import type { MovementRecord } from '@/store/slices/movementSlice';
import type { Citerne } from '@/types';
import type { Tier } from '@/types';
import { Input, Button, SearchSelect, Textarea } from '@/components/ui';

const schema = Yup.object({
  citerneId: Yup.string().required('Citerne obligatoire'),
  tierId: Yup.string().required('Client/Fournisseur obligatoire'),
  date: Yup.string().required('Date obligatoire'),
  prix: Yup.number().required('Prix obligatoire').min(0),
  quantite: Yup.number().required('Quantité obligatoire').min(0),
  remarque: Yup.string(),
});

interface VenteFormProps {
  vente?: MovementRecord | null;
  type: 'create' | 'update';
  onClose: () => void;
  onSuccess?: () => void;
}

export default function VenteForm({ vente, type, onClose, onSuccess }: VenteFormProps) {
  const dispatch = useAppDispatch();
  const citernes = useAppSelector((s: RootState) => s.citerne.citernes);
  const tiers = useAppSelector((s: RootState) => s.tier.tiers);

  useEffect(() => {
    dispatch(fetchCiternes());
    dispatch(fetchTiers());
  }, [dispatch]);

  const formik = useFormik({
    initialValues: {
      citerneId: vente?.citerne ? String((vente.citerne as { id?: number }).id) : '',
      tierId: vente?.tier ? String((vente.tier as { id?: number }).id) : '',
      date: vente?.date ? (vente.date as string).toString().slice(0, 10) : new Date().toISOString().slice(0, 10),
      prix: vente?.prix ?? '',
      quantite: vente?.quantite ?? '',
      remarque: (vente?.remarque as string) ?? '',
    },
    validationSchema: schema,
    onSubmit: async (values) => {
      const citerne = citernes.find((c: Citerne) => String(c.id) === values.citerneId);
      const tier = tiers.find((t: Tier) => String(t.id) === values.tierId);
      if (!citerne || !tier) return;
      const payload: MovementRecord = {
        citerne: { id: citerne.id, code: citerne.code },
        tier: { id: tier.id, code: tier.code ?? tier.nom },
        date: values.date,
        prix: Number(values.prix),
        quantite: Number(values.quantite),
        remarque: values.remarque || undefined,
      };
      if (type === 'update' && vente?.id) {
        const res = await dispatch(updateVente({ ...payload, id: vente.id }));
        if (updateVente.fulfilled.match(res)) {
          onSuccess?.();
          onClose();
        }
      } else {
        const res = await dispatch(createVente(payload));
        if (createVente.fulfilled.match(res)) {
          onSuccess?.();
          onClose();
        }
      }
    },
  });

  const citerneOptions = citernes.map((c: Citerne) => ({ value: String(c.id), label: c.code ?? String(c.id) }));
  const tierOptions = tiers.map((t: Tier) => ({ value: String(t.id), label: t.nom ?? t.code ?? String(t.id) }));

  return (
    <form onSubmit={formik.handleSubmit} className="flex flex-col gap-4">
     <div className="flex gap-4">
      <SearchSelect label="Citerne" options={citerneOptions} value={formik.values.citerneId} onChange={(v) => formik.setFieldValue('citerneId', v)} onBlur={() => formik.setFieldTouched('citerneId', true)} error={formik.touched.citerneId && formik.errors.citerneId ? String(formik.errors.citerneId) : undefined} searchPlaceholder="Rechercher une citerne…" />
      <SearchSelect label="Client / Fournisseur" options={tierOptions} value={formik.values.tierId} onChange={(v) => formik.setFieldValue('tierId', v)} onBlur={() => formik.setFieldTouched('tierId', true)} error={formik.touched.tierId && formik.errors.tierId ? String(formik.errors.tierId) : undefined} searchPlaceholder="Rechercher un client…" />
     </div>
      <div className="flex gap-4">
        <Input label="Date" type="date" id="date" {...formik.getFieldProps('date')} error={formik.touched.date && formik.errors.date ? String(formik.errors.date) : undefined} />
      <Input label="Prix" type="number" id="prix" {...formik.getFieldProps('prix')} error={formik.touched.prix && formik.errors.prix ? String(formik.errors.prix) : undefined} />
      <Input label="Quantité" type="number" id="quantite" {...formik.getFieldProps('quantite')} error={formik.touched.quantite && formik.errors.quantite ? String(formik.errors.quantite) : undefined} />
     </div>
        <Textarea label="Remarque" id="remarque" rows={3} {...formik.getFieldProps('remarque')} />
      <div className="flex justify-end gap-2 pt-2">
        <Button type="button" variant="outline" onClick={onClose}>Annuler</Button>
        <Button type="submit" loading={formik.isSubmitting}>{type === 'update' ? 'Modifier' : 'Créer'}</Button>
      </div>
    </form>
  );
}
